import React from 'react';
import { ContactItem, theme } from '@zonofi/common';
import { chatStyles } from '@zonofi/chats-common/styles';
import { Avatar } from '../../avatar';
import { ContactViewAllPopup } from './contact-view-all-popup';

interface ContactViewProps {
  contacts: ContactItem[];
  userName?: string;
}

export const ContactView: React.FC<ContactViewProps> = ({
  contacts,
  userName = '',
}) => {
  const firstContact = contacts[0];
  const visibleContacts = contacts.slice(0, 2); // Show first 2 names
  const remainingCount = contacts.length - 2;

  // Format contact names: "Name1, Name2 & 1 other"
  const formatContactNames = () => {
    const names = visibleContacts.map((c) => c.displayName || 'Unknown');
    if (remainingCount > 0) {
      return `${names.join(', ')} & ${remainingCount} other${remainingCount > 1 ? 's' : ''}`;
    }
    return names.join(', ');
  };

  return (
    <div
      key="contacts-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        margin: '-4px', // Extend to edges to account for message-template padding (4px)
        paddingTop: '4px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        paddingRight: '12px',
        width: 'calc(100% + 8px)', // Account for negative margins
        boxSizing: 'border-box',
        minWidth: 0, // Allow flex item to shrink below content size
        overflow: 'hidden', // Prevent content from overflowing
      }}
    >
      {/* First row: Contact name and phone number */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          alignItems: 'flex-start',
          width: '100%',
          fontSize: '12px',
          lineHeight: '16px',
        }}
      >
        <div
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            ...chatStyles.fontSemiBold12,
            color: '#2abb7f', // Green color from Figma
            whiteSpace: 'pre-wrap',
          }}
        >
          {firstContact?.displayName || 'Unknown'}
        </div>
        {firstContact?.phoneNumber && (
          <div
            style={{
              ...chatStyles.fontRegular12,
              color: '#8c8c8c', // Grey color from Figma
              flexShrink: 0,
            }}
          >
            {firstContact.phoneNumber}
          </div>
        )}
      </div>

      {/* Second row: Avatars and full contact list */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        {/* Avatars container with overlapping */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 0,
            paddingRight: '24px',
            position: 'relative',
            isolation: 'isolate',
            flexShrink: 0,
          }}
        >
          {contacts.slice(0, 3).map((contact, index) => (
            <div
              key={index}
              style={{
                marginRight: '-24px',
                position: 'relative',
                zIndex: 3 - index,
              }}
            >
              <Avatar
                id={contact.phoneNumber || contact.displayName || '-'}
                title={contact.displayName || 'Unknown'}
                variant="user"
                size="small"
              />
            </div>
          ))}
        </div>

        {/* Contact names - no ellipsis, wraps naturally */}
        <div
          style={{
            flex: '1 1 0%',
            minWidth: 0,
            ...chatStyles.fontRegular14Primary,
            color: '#000000',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {formatContactNames()}
        </div>
      </div>

      {/* Separator line */}
      <div
        style={{
          backgroundColor: '#f0f0f0',
          height: '1px',
          width: '100%',
          flexShrink: 0,
          minWidth: 0,
        }}
      />

      {/* View all button - centered */}
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        <ContactViewAllPopup
          contacts={contacts}
          userName={userName}
          trigger={
            <button
              type="button"
              style={{
                ...chatStyles.fontSemiBold14Primary,
                color: '#32a7e8',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                textAlign: 'center',
              }}
            >
              View all
            </button>
          }
        />
      </div>
    </div>
  );
};
